<spinner *ngIf="loading"></spinner>

<h6 class="mt-2 mb-0 fw-bold">{{ title }}</h6>
<hr class="mt-2 mb-4" />

<div class="row">
    <div class="col-sm-4">
        <div class="form-group mb-3">
            <label class="form-label" [for]="selectId">Care Programme <span class="text-red">*</span></label>
            <ng-select [labelForId]="selectId"
                       dropdownPosition="top"
                       placeholder="Select one option"
                       [items]="carePrograms"
                       bindLabel="title"
                       [clearable]="false"
                       [searchable]="false"
                       [(ngModel)]="selectedCareProgram"
                       (ngModelChange)="onCareProgramChanged()"
                       [disabled]="disabled"
                       [class.is-invalid]="showError && !selectedCareProgram"
            ></ng-select>
        </div>
    </div>
</div>

@if (showChildSection) {
    <h5 class="mt-5 mb-3" id="child-section-title">Your child</h5>
    <div class="row" [formGroup]="childGroup" role="group" aria-labelledby="child-section-title">
        <div class="col-md-4">
            <div class="form-group mb-3">
                <label class="form-label" for="child-first-name">First name @if (childMode === 'required') {<span class="text-red">*</span>}</label>
                <input id="child-first-name" type="text" class="form-control" formControlName="firstName"
                       placeholder="Enter first name here" autocomplete="off"
                       [class.is-invalid]="childGroup.controls.firstName.invalid && childGroup.controls.firstName.touched"
                       [attr.aria-required]="childMode === 'required'"
                       [attr.aria-describedby]="childGroup.controls.firstName.invalid && childGroup.controls.firstName.touched ? 'child-first-name-error' : null"/>
                <div id="child-first-name-error" class="invalid-feedback" aria-live="polite">
                    @if (childGroup.controls.firstName.invalid && childGroup.controls.firstName.touched) {First name is required}
                </div>
            </div>
        </div>
        <div class="col-md-4">
            <div class="form-group mb-3">
                <label class="form-label" for="child-date-of-birth">Date of birth @if (childMode === 'required') {<span class="text-red">*</span>}</label>
                <input #childDob id="child-date-of-birth" type="date" class="form-control" formControlName="dateOfBirth"
                       [max]="today" (click)="openPicker(childDob)" (input)="updateChildAge()" (change)="updateChildAge()"
                       [class.is-invalid]="childGroup.controls.dateOfBirth.invalid && childGroup.controls.dateOfBirth.touched"
                       [attr.aria-required]="childMode === 'required'"
                       [attr.aria-describedby]="childGroup.controls.dateOfBirth.invalid && childGroup.controls.dateOfBirth.touched ? 'child-date-of-birth-error' : null"/>
                <div id="child-date-of-birth-error" class="invalid-feedback" aria-live="polite">
                    @if (childGroup.controls.dateOfBirth.touched) {
                        @if (childGroup.controls.dateOfBirth.hasError('futureDate')) {Date of birth cannot be in the future}
                        @else if (childGroup.controls.dateOfBirth.hasError('required')) {Date of birth is required}
                    }
                </div>
            </div>
        </div>
        <div class="col-md-4">
            <div class="form-group mb-3">
                <label class="form-label" for="child-age">Age (Calculated)</label>
                <input id="child-age" formControlName="age" type="number" step="1" readonly aria-readonly="true"
                       class="form-control bg-light" placeholder="Age"/>
            </div>
        </div>
    </div>
}

@if (selectedCareProgram != null) {

    <h5 class="mt-5 mb-3">Care Programme Schedule</h5>
    <p *ngIf="!disabled && !readonly">Below you can find the default schedule for the selected care programme. You can rearrange the chapters as needed, but make sure there is at least 1 chapter per week.</p>
    <div class="row mt-4" *ngIf="changed && !disabled && !readonly">
        <div class="col-md-6">
            <div class="alert alert-info" role="alert">
                <b>Note</b> The schedule is different from the original one for this care programme.
            </div>
        </div>
        <div class="col-md-6 text-end">
            <a class="btn btn-link" (click)="resetModules()">Reset schedule to default</a>
        </div>
    </div>

    <div class="mt-3">
        <div cdkDropListGroup class="stage-container">
            <h6>Early Stage</h6>
            <div class="row">
                @for (week of stageItems.early | keyvalue; track week.key) {
                    <div class="col-3 pb-3">
                        <div class="gray-box h-100"
                             [ngClass]="week.value.length == 0 ? 'week-error' : ''"
                             cdkDropList
                             [cdkDropListDisabled]="disabled || readonly"
                             (cdkDropListDropped)="drop($event)"
                             [cdkDropListData]="week.value"
                        >
                            <h5>Week {{ +week.key }}</h5>
                            <div class="module-list">
                                @for (item of week.value; track $index) {
                                    <div class="module" cdkDrag [cdkDragDisabled]="disabled || readonly">
                                        {{ $index + 1}}.  {{ item.title }}
                                        <span [class.icon]="!disabled && !readonly">
                                            <a (click)="showModal(item.id)"><i style="font-size:16px" class="bi bi-info-circle"></i></a>
                                        </span>
                                        <div class="delete-icon" *ngIf="!disabled && !readonly">
                                            <a [confirmModal]="'Remove chapter'" [confirmMessage]="'The chapter ' + item.title + ' will be removed from the care programme. Are you sure?'" (confirmedClick)="removeItem('early', week.key, item.id)"><i style="font-size:16px" class="bi bi-x-circle-fill"></i></a>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>

        <div cdkDropListGroup class="stage-container">
            <h6>Mid Stage</h6>
            <div class="row">
                @for (week of stageItems.mid | keyvalue; track week.key) {
                    <div class="col-3 pb-3">
                        <div class="gray-box h-100"
                             [ngClass]="week.value.length == 0 ? 'week-error' : ''"
                             cdkDropList
                             [cdkDropListDisabled]="disabled || readonly"
                             (cdkDropListDropped)="drop($event)"
                             [cdkDropListData]="week.value"
                        >
                            <h5>Week {{ +week.key }}</h5>
                            <div class="module-list">
                                @for (item of week.value; track $index) {
                                    <div class="module" cdkDrag [cdkDragDisabled]="disabled || readonly">
                                        {{ $index + 1}}.  {{ item.title }}
                                        <span [class.icon]="!disabled && !readonly">
                                            <a (click)="showModal(item.id)"><i style="font-size:16px" class="bi bi-info-circle"></i></a>
                                        </span>
                                        <div class="delete-icon" *ngIf="!disabled && !readonly">
                                            <a [confirmModal]="'Remove chapter'" [confirmMessage]="'The chapter ' + item.title + ' will be removed from the care programme. Are you sure?'" (confirmedClick)="removeItem('mid', week.key, item.id)"><i style="font-size:16px" class="bi bi-x-circle-fill"></i></a>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>

        <div cdkDropListGroup class="stage-container">
            <h6>Late Stage</h6>
            <div class="row">
                @for (week of stageItems.late | keyvalue; track week.key) {
                    <div class="col-3 pb-3">
                        <div class="gray-box h-100"
                             [ngClass]="week.value.length == 0 ? 'week-error' : ''"
                             cdkDropList
                             [cdkDropListDisabled]="disabled || readonly"
                             (cdkDropListDropped)="drop($event)"
                             [cdkDropListData]="week.value"
                        >
                            <h5>Week {{ +week.key }}</h5>
                            <div class="module-list">
                                @for (item of week.value; track $index) {
                                    <div class="module" cdkDrag [cdkDragDisabled]="disabled || readonly">
                                        {{ $index + 1}}.  {{ item.title }}
                                        <span [class.icon]="!disabled && !readonly">
                                            <a (click)="showModal(item.id)"><i style="font-size:16px" class="bi bi-info-circle"></i></a>
                                        </span>
                                        <div class="delete-icon" *ngIf="!disabled && !readonly">
                                            <a [confirmModal]="'Remove chapter'" [confirmMessage]="'The chapter ' + item.title + ' will be removed from the care programme. Are you sure?'" (confirmedClick)="removeItem('late', week.key, item.id)"><i style="font-size:16px" class="bi bi-x-circle-fill"></i></a>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                }
            </div>
        </div>
    </div>

}

